import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';

/**
 * XmlSchemaStep - Step 6: Upload/paste XML template or copy from reference.
 * After auto-mapping, displays an editable table for manual corrections.
 */
const XmlSchemaStep = ({ wizard, api }) => {
  const [mode, setMode] = useState('upload'); // 'upload' or 'reference'
  const [xmlContent, setXmlContent] = useState('');
  const [fields, setFields] = useState(null);
  const [fieldKeys, setFieldKeys] = useState([]);
  const [summary, setSummary] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  // Fetch available IRIS field keys for autocomplete
  useEffect(() => {
    if (fields && wizard.sessionId) {
      const fetchFieldKeys = async () => {
        try {
          const data = await api.getFieldKeys(wizard.sessionId, wizard.selectedRulesetId);
          setFieldKeys(data.fieldKeys || []);
        } catch {
          // Non-critical — autocomplete just won't work
        }
      };
      fetchFieldKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, wizard.sessionId, wizard.selectedRulesetId]);

  const handleParse = async () => {
    try {
      const schemaData = mode === 'reference'
        ? { xmlContent: '', schemaSource: 'reference', referenceCustomerId: wizard.referenceCustomerId }
        : { xmlContent, schemaSource: 'upload', referenceCustomerId: null };

      const data = await api.parseXmlSchema(wizard.sessionId, schemaData);
      setFields(data.fields || []);
      setSummary({ total: data.totalFields, mapped: data.mappedFields, unmapped: data.unmappedFields });
    } catch {
      // Error shown by hook
    }
  };

  const handleFieldChange = (index, property, value) => {
    setFields(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [property]: value };

      // Auto-update mapped status
      if (property === 'isIrisCaptureField' || property === 'irisFieldId') {
        const field = updated[index];
        updated[index].isMapped = field.isIrisCaptureField || field.isMetaDataField;
      }

      return updated;
    });
  };

  const handleConfirm = () => {
    // Regenerate JSON from current field state
    const schemaEntries = fields.map(f => ({
      DataType: f.dataType || 'string',
      IrisFieldId: f.irisFieldId || f.fieldXMLName,
      MetadataKey: f.isMetaDataField ? (f.metadataKey || f.fieldXMLName) : null,
      FieldXMLName: f.fieldXMLName,
      IsDetailField: f.isDetailField || false,
      IsReportField: false,
      IsMetaDataField: f.isMetaDataField || false,
      IsIrisCaptureField: f.isIrisCaptureField || false
    }));

    const generatedSchemaJson = JSON.stringify(schemaEntries);
    setConfirmed(true);
    wizard.completeStep(6, JSON.stringify({ generatedSchemaJson }));
  };

  // After confirmation — show summary
  if (confirmed) {
    const mappedCount = fields.filter(f => f.isIrisCaptureField || f.isMetaDataField).length;
    return (
      <div>
        <Alert variant="success">
          <Alert.Heading>Schema Confirmed</Alert.Heading>
          <p>{fields.length} total fields — {mappedCount} mapped. Click "Next" to continue.</p>
        </Alert>
      </div>
    );
  }

  // Editable mapping table
  if (fields) {
    const mappedCount = fields.filter(f => f.isIrisCaptureField || f.isMetaDataField).length;
    const unmappedCount = fields.length - mappedCount;

    return (
      <div>
        <Alert variant="info">
          <Alert.Heading>Review Field Mappings</Alert.Heading>
          <p>{fields.length} fields detected — {mappedCount} mapped, {unmappedCount} unmapped. Edit below then confirm.</p>
        </Alert>

        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <Table striped bordered size="sm" responsive>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
              <tr>
                <th>XML Field</th>
                <th>IRIS Field ID</th>
                <th>Type</th>
                <th>Data Type</th>
                <th>Metadata</th>
                <th>IRIS Capture</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => {
                const isUnmapped = !field.isIrisCaptureField && !field.isMetaDataField;
                return (
                  <tr key={i} className={isUnmapped ? 'table-warning' : ''}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{field.fieldXMLName}</td>
                    <td>
                      <Form.Control
                        id={`field-iris-id-${i}`}
                        size="sm"
                        type="text"
                        list="iris-field-keys"
                        value={field.irisFieldId || ''}
                        onChange={(e) => handleFieldChange(i, 'irisFieldId', e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                        aria-label={`IRIS Field ID for ${field.fieldXMLName}`}
                      />
                    </td>
                    <td>
                      {field.isDetailField && <Badge bg="info">Detail</Badge>}
                      {field.isMetaDataField && <Badge bg="warning">Meta</Badge>}
                      {!field.isDetailField && !field.isMetaDataField && <Badge bg="secondary">Header</Badge>}
                    </td>
                    <td>
                      <Form.Select
                        id={`field-data-type-${i}`}
                        size="sm"
                        value={field.dataType || 'string'}
                        onChange={(e) => handleFieldChange(i, 'dataType', e.target.value)}
                        aria-label={`Data type for ${field.fieldXMLName}`}
                      >
                        <option value="string">string</option>
                        <option value="number">number</option>
                      </Form.Select>
                    </td>
                    <td className="text-center">
                      <Form.Check
                        id={`field-metadata-${i}`}
                        type="checkbox"
                        checked={field.isMetaDataField || false}
                        onChange={(e) => handleFieldChange(i, 'isMetaDataField', e.target.checked)}
                        aria-label={`Metadata toggle for ${field.fieldXMLName}`}
                      />
                    </td>
                    <td className="text-center">
                      <Form.Check
                        id={`field-iris-capture-${i}`}
                        type="checkbox"
                        checked={field.isIrisCaptureField || false}
                        onChange={(e) => handleFieldChange(i, 'isIrisCaptureField', e.target.checked)}
                        aria-label={`IRIS capture toggle for ${field.fieldXMLName}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        {/* Datalist for autocomplete */}
        <datalist id="iris-field-keys">
          {fieldKeys.map((key) => (
            <option key={key} value={key} />
          ))}
        </datalist>

        <div className="mt-3">
          <Button variant="success" onClick={handleConfirm}>
            Confirm Mapping
          </Button>
          <Button variant="outline-secondary" className="ms-2" onClick={() => { setFields(null); setSummary(null); }}>
            Re-upload
          </Button>
        </div>
      </div>
    );
  }

  // Initial upload/reference mode
  return (
    <div>
      <h5>Step 6: XML Schema Template</h5>
      <p className="text-muted">Provide an XML template to generate the data capture schema, or copy from the reference customer.</p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}

      <Form.Group className="mb-3">
        <Form.Check
          id="xml-mode-upload"
          type="radio"
          label="Upload/Paste XML Template"
          checked={mode === 'upload'}
          onChange={() => setMode('upload')}
        />
        <Form.Check
          id="xml-mode-reference"
          type="radio"
          label="Copy from Reference Customer"
          checked={mode === 'reference'}
          onChange={() => setMode('reference')}
          disabled={!wizard.referenceCustomerId}
        />
      </Form.Group>

      {mode === 'upload' && (
        <Form.Group className="mb-3">
          <Form.Label htmlFor="xml-file-upload">XML Template Content</Form.Label>
          <div className="mb-2">
            <Form.Control
              id="xml-file-upload"
              type="file"
              accept=".xml,.txt"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setXmlContent(ev.target.result);
                  reader.readAsText(file);
                }
              }}
            />
            <Form.Text className="text-muted">Upload an XML file, or paste content below.</Form.Text>
          </div>
          <Form.Control
            id="xml-content-textarea"
            as="textarea"
            rows={10}
            value={xmlContent}
            onChange={(e) => setXmlContent(e.target.value)}
            placeholder="Paste XML template here..."
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            aria-label="XML template content"
          />
        </Form.Group>
      )}

      {mode === 'reference' && (
        <Alert variant="info">
          Schema will be copied from the reference customer's InvoiceDataCapture job record and remapped to the new customer's extraction fields.
        </Alert>
      )}

      <Button
        variant="primary"
        onClick={handleParse}
        disabled={api.loading || (mode === 'upload' && !xmlContent.trim())}
      >
        {api.loading ? <><Spinner size="sm" className="me-2" />Processing...</> : 'Generate Schema'}
      </Button>
    </div>
  );
};

export default XmlSchemaStep;
